import React, { useMemo } from 'react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import Text, { FontStyle } from '~/components/core/Text'
import Ellipse from '~/components/Ellipse'
import { light } from '~/constants/theme'
import { CallTranscript } from '~/types/CallTranscript'
import { isInRange } from '~/utils/math'

interface SentimentByQuarterStatWrapperProps {
  width?: number
  margin?: string
}

const SentimentByQuarterStatWrapper = styled.div<SentimentByQuarterStatWrapperProps>`
  display: flex;
  flex-direction: column;
  width: ${(props) => props.width}px;
  margin: ${(props) => props.margin ?? ''};
`

const LineChartWrapper = styled.div`
  display: flex;
  margin: 0px 5px 0px -35px;
`

interface SentimentByQuarterStatProps {
  transcript: CallTranscript
  width?: number
  margin?: string
}

enum SentimentDataKeys {
  AGENT = 'agentScore',
  CUSTOMER = 'customerScore',
}

interface ChartDotProps<PayloadType> {
  payload?: PayloadType
  dataKey?: string
  cx: number
  cy: number
  index?: number
}

const CenteredStatsColumn = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

//prettier-ignore
const StatsWrapper = styled.div<Omit<SentimentByQuarterStatWrapperProps, 'margin'>>`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  width: ${(props) => props.width}px;
  height: 15px;
  margin-bottom: 1px;
`

const ChartDot = ({
  payload,
  dataKey,
  cx,
  cy,
}: ChartDotProps<{
  payload: { agentScore: number; customerScore: number }
  dataKey: string
  cx: number
  cy: number
  index?: number
}>) => {
  const score = payload![dataKey as SentimentDataKeys]
  const dotColor = isInRange(score, 0, 2)
    ? light.dark700
    : score < 0
      ? light.red100
      : light.green600

  return (
    <svg
      x={cx - 6}
      y={cy - 6}
      width={12}
      height={12}
      stroke={light.gray200}
      strokeWidth={1}
    >
      <g transform="translate(6 6)">
        <circle r="5" fill={dotColor} />
      </g>
    </svg>
  )
}

const getChartDot = ({
  payload,
  dataKey,
  cx,
  cy,
  index,
}: ChartDotProps<{
  payload: { agentScore: number; customerScore: number }
  dataKey: string
  cx: number
  cy: number
  index: number
}>) => {
  return <ChartDot key={index} {...{ payload, dataKey, cx, cy }} />
}

const formatSentimentValue = (value: number) => {
  return value > 0 ? `+${value}` : value.toString()
}

const defaultSentimentValue: { Score: number }[] = [
  { Score: 0 },
  { Score: 0 },
  { Score: 0 },
  { Score: 0 },
]

const SentimentByQuarterStat = ({
  transcript,
  width,
  margin,
}: SentimentByQuarterStatProps) => {
  const agentSentiment =
    transcript.ConversationCharacteristics.Sentiment.SentimentByPeriod.QUARTER
      .AGENT ?? defaultSentimentValue
  const customerSentiment =
    transcript.ConversationCharacteristics.Sentiment.SentimentByPeriod.QUARTER
      .CUSTOMER ?? defaultSentimentValue

  //graph data needs to be memoized or the dots will not render all the time:
  //https://github.com/recharts/recharts/issues/1821 (second to last comment)
  const graphData: { agentScore: number; customerScore: number }[] =
    useMemo(() => {
      const data: { agentScore: number; customerScore: number }[] = []
      for (let i = 0; i < agentSentiment.length; i++) {
        data.push({
          agentScore: agentSentiment[i].Score,
          customerScore: customerSentiment[i].Score,
        })
      }
      return data
    }, [transcript])

  const customerColor = light.dark900
  const agentColor = light.dark600

  const overallCustomerScore =
    transcript.ConversationCharacteristics.Sentiment.OverallSentiment
      .CUSTOMER ?? 0
  const overallAgentScore =
    transcript.ConversationCharacteristics.Sentiment.OverallSentiment.AGENT ?? 0
  return (
    <SentimentByQuarterStatWrapper width={width} margin={margin}>
      <Text fontStyle={FontStyle.ButtonBold} style={{ marginBottom: '-5px' }}>
        Sentiment by Quarter
      </Text>
      <LineChartWrapper>
        <LineChart
          width={width! + 40}
          height={150}
          margin={{ top: 12, right: 12, bottom: 12 }}
          style={{
            fontFamily: 'Inter',
            fontStyle: 'normal',
            fontSize: '11px',
          }}
          data={graphData}
        >
          <CartesianGrid
            stroke={light.dark600}
            strokeDasharray="2 2"
            vertical={false}
            horizontalPoints={[21, 76, 126]}
          />
          <XAxis hide />
          <YAxis
            tickLine={false}
            axisLine={false}
            tickMargin={4}
            tickFormatter={formatSentimentValue}
            ticks={[-5, -2.5, 0, 2.5, 5]}
          />
          <Line
            type="linear"
            dataKey="agentScore"
            stroke={agentColor}
            strokeWidth={1}
            dot={(props) => getChartDot(props)}
          />
          <Line
            type="linear"
            dataKey="customerScore"
            stroke={customerColor}
            strokeWidth={2}
            dot={(props) => getChartDot(props)}
          />
        </LineChart>
      </LineChartWrapper>
      <CenteredStatsColumn>
        <StatsWrapper width={180}>
          <Ellipse
            height={4}
            width={16}
            color={agentColor}
            margin="0px 5px 0px 0px"
          />
          <Text fontStyle={FontStyle.TinySmall}>
            Agent: {formatSentimentValue(overallAgentScore)} Overall
          </Text>
        </StatsWrapper>
        <StatsWrapper width={180}>
          <Ellipse
            height={6}
            width={16}
            color={customerColor}
            margin="0px 5px 0px 0px"
          />
          <Text fontStyle={FontStyle.TinySmall}>
            Customer: {formatSentimentValue(overallCustomerScore)} Overall
          </Text>
        </StatsWrapper>
      </CenteredStatsColumn>
    </SentimentByQuarterStatWrapper>
  )
}

export default SentimentByQuarterStat
